import NiotaClassicApiService from '@/services/niotaClassicApiService';
import debounce from 'lodash/debounce';
import { mapState } from 'vuex';
import { vuetifyColors } from '@/utils/themes.js';
import { userItemsPerListMixin, isCopyToClipboardSupported, copyToClipboard } from '@/utils/vuex';
import { localizeDate } from '@/utils/date';
import listMixin from '../listMixin';
import get from 'lodash/get';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['title'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
export default {
  name: 'NiotaClassicDeviceMonitoringList',
  mixins: [listMixin, userItemsPerListMixin],
  props: {
    accountId: {
      type: Number,
      required: false,
      default: undefined
    },
    filterAlert: {
      type: Boolean,
      required: false,
      default: false
    },
    filterWarning: {
      type: Boolean,
      required: false,
      default: false
    },
    filterThreshold: {
      type: Boolean,
      required: false,
      default: false
    },
    resetFilter: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      isFetching: false,
      range: '1d',
      tableOptions: {
        ...defaultTableOptions
      },
      list: [],
      searchItem: null,
      filteredList: [],
      expandedItems: [],
      apiError: null
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    tableLocalizations() {
      return {
        headers: [{
          sortable: false
        }, {
          text: this.$i18n_t('NiotaClassic.Common.table.id'),
          value: 'id',
          align: 'right'
        }, {
          text: this.$i18n_t('NiotaClassic.Common.table.title'),
          value: 'title'
        }, {
          text: this.$i18n_t('NiotaClassic.Common.table.eui'),
          value: 'eui'
        }, {
          text: this.$i18n_t('NiotaClassic.DeviceMonitoring.application'),
          value: 'applicationTitle',
          sortable: false
        }, {
          text: this.$i18n_t('NiotaClassic.DeviceMonitoring.additionalInformation'),
          value: 'additionalInformation',
          sortable: true
        }, {
          text: this.$i18n_t('NiotaClassic.DeviceMonitoring.lastPacket'),
          value: 'createdAt'
        }, {
          sortable: false
        }],
        tableFooterProps: {
          itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
          itemsPerPageOptions: [defaultItemsPerPage, 20, 50]
        }
      };
    }
  },
  watch: {
    accountId: debounce(function () {
      // Items have already been requested
      if (this.isFetching) {
        return;
      }
      this.fetchList();
    }, 500),
    filterAlert() {
      this.filterList();
    },
    filterWarning() {
      this.filterList();
    },
    filterThreshold() {
      this.filterList();
    },
    resetFilter() {
      this.resetAllFilter();
    }
  },
  created() {
    defaultTableOptions.itemsPerPage = this.savedUserItemsPerList();
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    buildAdditionalInformation(data) {
      return data.map(item => {
        let infoLine = '';
        if (item.battery_alert_status) {
          infoLine = `${this.$i18n_t('NiotaClassic.DeviceMonitoring.battery')}: ${item.battery_value.value.toFixed(2)} - ${this.$i18n_t('NiotaClassic.DeviceMonitoring.alertLevel')}: ${item.variable.alert_level}`;
        } else if (!item.battery_alert_status && item.battery_warning_status) {
          infoLine = `${this.$i18n_t('NiotaClassic.DeviceMonitoring.battery')}: ${item.battery_value.value.toFixed(2)} - ${this.$i18n_t('NiotaClassic.DeviceMonitoring.warningLevel')}: ${item.variable.warning_level}`;
        } else if (!item.battery_alert_status && !item.battery_warning_status && item.threshold > item.packetCount) {
          infoLine = `${this.$i18n_t('NiotaClassic.DeviceMonitoring.sent')}: ${item.packetCount} - ${this.$i18n_t('NiotaClassic.DeviceMonitoring.threshold')}: ${item.threshold}`;
        }
        return {
          ...item,
          additionalInformation: infoLine
        };
      });
    },
    fetchList() {
      this.isFetching = true;
      NiotaClassicApiService.deviceMonitoring(this.range, this.accountId).then(res => {
        this.list = this.buildAdditionalInformation(res.filteredDevices);
        this.filterList();
      }).catch(error => {
        // this.$snack(this.$i18n_t('NiotaClassic.DeviceMonitoring.notFound'), 'error');
        this.list = [];
        this.filterList();
        if (get(error, 'response.data.err') === 'Influx Integration is not activated') {
          // if influx integration is not activated, show error
          this.apiError = this.$i18n_t('NiotaClassic.DeviceMonitoring.errorInfluxNotActive');
        } else if (error.code === 'ECONNABORTED') {
          // timeouts will display the respective error message
          this.apiError = this.$i18n_t('NiotaClassic.DeviceMonitoring.errorTimeout');
        } else if (error.response.status !== 404) {
          // other errors than 404 will display 'unknown error' message
          this.apiError = this.$i18n_t('NiotaClassic.DeviceMonitoring.errorUnknown');
        }
      }).finally(() => {
        this.isFetching = false;
      });
    },
    getColor(type) {
      return vuetifyColors[type] || vuetifyColors['primary'];
    },
    localizeDate(item) {
      if (item) {
        return localizeDate(item);
      }
    },
    filterList() {
      // filter by filter Btns
      if (this.filterAlert) {
        this.filteredList = this.list.filter(item => {
          return item.battery_alert_status;
        });
      } else if (this.filterWarning) {
        this.filteredList = this.list.filter(item => {
          return !item.battery_alert_status && item.battery_warning_status;
        });
      } else if (this.filterThreshold) {
        this.filteredList = this.list.filter(item => {
          return !item.battery_alert_status && !item.battery_warning_status && item.threshold > item.packetCount;
        });
      } else {
        this.filteredList = this.list;
      }
      this.$emit('filteredList', this.filteredList);
    },
    resetAllFilter() {
      this.filteredList = this.list;
    },
    getFiltered(newList) {
      this.$emit('filteredList', newList);
    },
    openDetails(item) {
      this.$router.push(`/niota-classic/devices/${item.id}/details`);
    },
    handleClickDevice(item) {
      if (this.expandedItems.length === 0 || this.expandedItems[0].id !== item.id) {
        this.expandedItems = [item];
      } else {
        this.expandedItems = [];
      }
    },
    isCopySupported() {
      return isCopyToClipboardSupported();
    },
    copyToClipboard(event, copyValue) {
      event.stopPropagation();
      copyToClipboard(copyValue);
    }
  }
};